import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  Globe,
  Phone,
  Mail,
  Calendar,
  Users,
  Award,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
  Target,
  Eye,
  Heart,
  Sparkles,
  Search,
  SlidersHorizontal,
  ArrowRight,
  X,
  CheckCircle,
  Star,
  Send,
  Clock,
  Ticket,
} from "lucide-react";
import { IOrganizationProps } from "../../../../types/Organization";
import { IEventProps } from "../../../../types/Event";
import { usePerformRequest } from "../../../../lib/usePerformRequest";
import { DashboardEndpoints } from "../../../../lib/endpoints-dashboard";
import { Loader } from "../../../../components/ui/loader";
import "./styles.scss";

interface HomepageThemeThreeProps {
  organizationId: number;
  primaryColor?: string;
  secondaryColor?: string;
}

// Convert IEventProps to Event format
const convertToEvent = (event: IEventProps) => {
  const formattedDate =
    event.date && !isNaN(new Date(event.date).getTime())
      ? event.date
      : new Date().toISOString();

  const getLocationString = (): string => {
    if (event.location) {
      if (event.type === "online") return "Online Event";
      if (event.type === "hybrid") return "Hybrid Event";
      if (event.location.city) return event.location.city;
      if (event.location.address) return event.location.address;
    }
    return "Location TBA";
  };

  return {
    id: event.id.toString(),
    title: event.name,
    description: event.event_description || "",
    date: formattedDate,
    time: "TBD", // IEventProps doesn't have start_time/end_time
    location: getLocationString(),
    image: event.cover_image || "",
    category: "General", // IEventProps doesn't have category
    attendees: (event as any).event_attendees_count || 0,
    maxAttendees: event.number_of_seats || undefined,
    isPaid: event.is_paid_event,
    type: event.type,
  };
};

export const HomepageThemeThree: React.FC<HomepageThemeThreeProps> = ({
  organizationId,
  primaryColor = "#17265a",
  secondaryColor = "#ffffff",
}) => {
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");

  // Set CSS variables for dynamic theming
  React.useEffect(() => {
    document.documentElement.style.setProperty(
      "--theme-primary-color",
      primaryColor
    );
    document.documentElement.style.setProperty(
      "--theme-secondary-color",
      secondaryColor
    );
  }, [primaryColor, secondaryColor]);

  // Fetch organization data
  const organizationEndpoint = useMemo(() => {
    return DashboardEndpoints.GetSingleOrganization(organizationId);
  }, [organizationId]);

  const { data: orgResponse, isLoading: isLoadingOrg } =
    usePerformRequest<IOrganizationProps>({
      method: organizationEndpoint.method as any,
      route: organizationEndpoint.route,
      doNotUseAuthorization: true,
    });

  const organization = useMemo(() => {
    if (!orgResponse) return null;
    const data = (orgResponse as any)?.data || orgResponse;
    return data as IOrganizationProps;
  }, [orgResponse]);

  // Fetch events data
  const eventsEndpoint = useMemo(() => {
    if (!organizationId) return null;
    return DashboardEndpoints.GetOrganizationEvents(organizationId);
  }, [organizationId]);

  const { data: eventsResponse, isLoading: isLoadingEvents } =
    usePerformRequest<IEventProps[]>({
      method: eventsEndpoint?.method as any,
      route: eventsEndpoint?.route || "",
    });

  // Extract events
  const events = useMemo(() => {
    if (!eventsResponse) {
      return [];
    }

    // Handle different response structures
    let data = eventsResponse;

    // Check if it's a paginated response
    if ((eventsResponse as any)?.data) {
      data = (eventsResponse as any).data;
    }

    // Check if data is an array
    if (Array.isArray(data)) {
      return data.map(convertToEvent);
    }

    // Check if events are nested in an object
    if (data && typeof data === "object" && (data as any).events) {
      const eventsArray = Array.isArray((data as any).events)
        ? ((data as any).events as IEventProps[])
        : [];
      return eventsArray.map(convertToEvent);
    }

    // Check if it's a single event wrapped in an array
    if (data && typeof data === "object" && !Array.isArray(data)) {
      return [convertToEvent(data as IEventProps)];
    }

    return [];
  }, [eventsResponse]);

  // Social links
  const socialLinks = useMemo(() => {
    if (!organization) return [];
    return [
      { icon: Facebook, url: organization.facebook_link, label: "Facebook" },
      { icon: Instagram, url: organization.instagram_link, label: "Instagram" },
      { icon: Linkedin, url: organization.linkedin_link, label: "LinkedIn" },
      { icon: Twitter, url: organization.x_link, label: "Twitter" },
      { icon: Youtube, url: organization.youtube_link, label: "YouTube" },
    ].filter((link) => link.url);
  }, [organization]);

  const locationString = useMemo(() => {
    if (!organization?.location) return "";
    return [
      organization.location.address,
      organization.location.city,
      organization.location.state,
      organization.location.country,
    ]
      .filter(Boolean)
      .join(", ");
  }, [organization?.location]);

  // Filter events
  const categories = useMemo(
    () => ["All", ...Array.from(new Set(events.map((e) => e.category)))],
    [events]
  );

  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      const matchesSearch =
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory =
        selectedCategory === "All" || event.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [events, searchTerm, selectedCategory]);

  const upcomingEvents = filteredEvents.filter(
    (event) => new Date(event.date) >= new Date()
  );
  const pastEvents = filteredEvents.filter(
    (event) => new Date(event.date) < new Date()
  );

  if (isLoadingOrg || !organization) {
    return (
      <div className="theme-three-loader">
        <Loader />
      </div>
    );
  }

  return (
    <div className="theme-three">
      {/* Hero Section with Cover Image */}
      <section className="theme-three-hero">
        <div className="hero-cover-wrapper">
          <motion.div
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="hero-cover-image"
          >
            {organization.logo ? (
              <img src={organization.logo} alt="Organization cover" />
            ) : (
              <div className="hero-cover-placeholder" />
            )}
          </motion.div>

          {/* Gradient Overlays */}
          <div className="hero-gradient-overlay-1" />
          <div className="hero-gradient-overlay-2" />

          {/* Floating Stats Badge */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="hero-stats-badge"
          >
            <div className="stats-badge-content">
              <div className="stats-badge-icon-wrapper">
                <Star className="stats-badge-icon" />
              </div>
              <div className="stats-badge-text">
                <div className="stats-badge-rating">4.9/5</div>
                <div className="stats-badge-label">Rating</div>
              </div>
            </div>
            <div className="stats-badge-followers">
              {(organization as any).followers?.toLocaleString() || "12,543"}{" "}
              followers
            </div>
          </motion.div>

          {/* Bottom Content */}
          <div className="hero-bottom-content">
            <div className="hero-content-container">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1,
                    transition: {
                      staggerChildren: 0.1,
                      delayChildren: 0.2,
                    },
                  },
                }}
                className="hero-content-inner"
              >
                {/* Animated Logo */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, y: 50 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    ease: [0.6, 0.05, 0.01, 0.9],
                  }}
                  className="hero-logo-wrapper"
                >
                  <div className="hero-logo-container">
                    {organization.logo ? (
                      <img
                        src={organization.logo}
                        alt={`${organization.name} logo`}
                        className="hero-logo"
                      />
                    ) : (
                      <div className="hero-logo-placeholder" />
                    )}
                    <div className="hero-logo-hover-overlay" />
                  </div>

                  {/* Verified Badge */}
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 1, duration: 0.5, ease: "easeOut" }}
                    className="hero-verified-badge"
                  >
                    <CheckCircle className="verified-icon" />
                  </motion.div>
                </motion.div>

                {/* Name and Quick Actions */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="hero-text-content"
                >
                  <div className="hero-text-inner">
                    <div className="hero-name-section">
                      <h1 className="hero-name">{organization.name}</h1>
                      <p className="hero-tagline">{organization.subtitle}</p>
                    </div>

                    {/* CTA Buttons */}
                    <div className="hero-cta-buttons">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="hero-cta-primary"
                      >
                        Follow
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="hero-cta-secondary"
                      >
                        Contact
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Info Bar */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.6 }}
        className="theme-three-info-bar"
      >
        <div className="info-bar-container">
          <div className="info-bar-grid">
            {/* Description */}
            <div className="info-bar-description">
              <p>{organization.description}</p>
            </div>

            {/* Contact and Social */}
            <div className="info-bar-contact">
              {/* Contact Links */}
              <div className="info-bar-contact-links">
                {locationString && (
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="info-bar-contact-item"
                  >
                    <MapPin className="info-bar-icon" />
                    <span>{locationString}</span>
                  </motion.div>
                )}
                {organization.website && (
                  <motion.a
                    whileHover={{ scale: 1.05 }}
                    href={organization.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="info-bar-contact-item"
                  >
                    <Globe className="info-bar-icon" />
                    <span>Website</span>
                  </motion.a>
                )}
              </div>

              <div className="info-bar-contact-links">
                {organization.email && (
                  <motion.a
                    whileHover={{ scale: 1.05 }}
                    href={`mailto:${organization.email}`}
                    className="info-bar-contact-item"
                  >
                    <Mail className="info-bar-icon" />
                    <span>{organization.email}</span>
                  </motion.a>
                )}
                {organization.phone_number && (
                  <motion.a
                    whileHover={{ scale: 1.05 }}
                    href={`tel:${organization.phone_number}`}
                    className="info-bar-contact-item"
                  >
                    <Phone className="info-bar-icon" />
                    <span>{organization.phone_number}</span>
                  </motion.a>
                )}
              </div>

              {/* Social Links */}
              {socialLinks.length > 0 && (
                <div className="info-bar-social">
                  {socialLinks.map((social, index) => {
                    const Icon = social.icon;
                    return (
                      <motion.a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={social.url}
                        whileHover={{ scale: 1.1, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                        className="info-bar-social-link"
                      >
                        <Icon className="info-bar-social-icon" />
                      </motion.a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Stats Section */}
      <section className="theme-three-stats">
        <div className="stats-container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.15,
                  delayChildren: 0.2,
                },
              },
            }}
            className="stats-grid"
          >
            {[
              {
                icon: Calendar,
                value: (
                  (organization as any).event_count ??
                  events.length ??
                  0
                ).toLocaleString(),
                label: "Events Hosted",
              },
              {
                icon: Users,
                value:
                  (
                    (organization as any).event_attendees_count ?? 0
                  ).toLocaleString() + "+",
                label: "Total Attendees",
              },
              {
                icon: MapPin,
                value: (organization.number_of_locations ?? 0).toString(),
                label: "Locations",
              },
              {
                icon: Award,
                value: (organization.years_of_experience ?? 0).toString() + "+",
                label: "Years of Excellence",
              },
            ].map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  variants={{
                    hidden: { opacity: 0, y: 30 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.6,
                        ease: "easeOut",
                      },
                    },
                  }}
                  className="stats-item"
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                    className="stats-icon-wrapper"
                  >
                    <Icon className="stats-icon" />
                  </motion.div>
                  <motion.div
                    className="stats-value"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{
                      type: "spring",
                      stiffness: 200,
                      damping: 15,
                      delay: index * 0.15 + 0.4,
                    }}
                  >
                    {stat.value}
                  </motion.div>
                  <div className="stats-label">{stat.label}</div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="theme-three-about">
        <div className="about-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="about-header"
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="about-badge"
            >
              <Sparkles className="about-badge-icon" />
              <span>Who We Are</span>
            </motion.div>
            <h2 className="about-title">About Us</h2>
            {organization.about?.about ? (
              <p className="about-description">{organization.about.about}</p>
            ) : organization.description ? (
              <p className="about-description">{organization.description}</p>
            ) : null}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.15,
                  delayChildren: 0.2,
                },
              },
            }}
            className="about-cards-grid"
          >
            {[
              {
                icon: Target,
                title: "Our Mission",
                description: organization.about?.mission || "",
                color: "from-blue-500",
              },
              {
                icon: Eye,
                title: "Our Vision",
                description: organization.about?.vision || "",
                color: "from-indigo-500",
              },
              {
                icon: Heart,
                title: "Our Values",
                description: organization.about?.values || "",
                color: "from-violet-500",
              },
            ]
              .filter((value) => value.description)
              .map((value, index) => {
                const Icon = value.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 40, rotateX: -15 }}
                    whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{
                      duration: 0.7,
                      ease: [0.6, 0.05, 0.01, 0.9],
                      delay: index * 0.15,
                    }}
                    className="about-card"
                  >
                    <motion.div
                      whileHover={{ y: -12 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                      className="about-card-inner"
                    >
                      <div className="about-card-content">
                        <div className="about-card-icon-section">
                          <motion.div
                            whileHover={{ scale: 1.1, rotate: [0, -10, 10, 0] }}
                            transition={{ duration: 0.5 }}
                            className={`about-card-icon-wrapper ${value.color}`}
                          >
                            <div>
                              <Icon className="about-card-icon" />
                            </div>
                          </motion.div>
                          <div className="about-card-dots">
                            {[...Array(3)].map((_, i) => (
                              <motion.div
                                key={i}
                                className="about-card-dot"
                                initial={{ scale: 0 }}
                                whileInView={{ scale: 1 }}
                                viewport={{ once: true }}
                                transition={{
                                  delay: index * 0.15 + i * 0.1 + 0.5,
                                }}
                              />
                            ))}
                          </div>
                        </div>

                        <div className="about-card-text">
                          <h3 className="about-card-title">{value.title}</h3>
                          <p className="about-card-description">
                            {value.description}
                          </p>
                          <motion.div
                            className={`about-card-accent ${value.color}`}
                            initial={{ width: 0 }}
                            whileInView={{ width: "60%" }}
                            viewport={{ once: true }}
                            transition={{
                              delay: index * 0.15 + 0.8,
                              duration: 0.8,
                              ease: "easeOut",
                            }}
                          />
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                );
              })}
          </motion.div>
        </div>
      </section>

      {/* Events Section */}
      <section className="theme-three-events">
        <div className="events-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="events-header"
          >
            <h2 className="events-title">Events</h2>

            {/* Filters */}
            <div className="events-filters">
              {/* Search */}
              <motion.div
                className="events-search-wrapper"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Search className="events-search-icon" />
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="events-search-input"
                />
              </motion.div>

              {/* Category Filter */}
              <motion.div
                className="events-category-wrapper"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <SlidersHorizontal className="events-category-icon" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="events-category-select"
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </motion.div>
            </div>
          </motion.div>

          {/* Upcoming Events */}
          {upcomingEvents.length > 0 && (
            <div className="events-section">
              <motion.h3
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="events-section-title"
              >
                Upcoming Events
              </motion.h3>
              <div className="events-grid">
                {upcomingEvents.map((event, index) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    index={index}
                    onViewMore={() => {
                      setSelectedEvent(event);
                      setIsModalOpen(true);
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Past Events */}
          {pastEvents.length > 0 && (
            <div className="events-section">
              <motion.h3
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="events-section-title"
              >
                Past Events
              </motion.h3>
              <div className="events-grid events-grid-past">
                {pastEvents.map((event, index) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    index={index}
                    onViewMore={() => {
                      setSelectedEvent(event);
                      setIsModalOpen(true);
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {filteredEvents.length === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="events-empty"
            >
              <p className="events-empty-text">
                No events found matching your criteria.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="theme-three-footer">
        <div className="footer-container">
          {/* Newsletter Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="footer-newsletter"
          >
            <div className="footer-newsletter-content">
              <div className="footer-newsletter-text">
                <h3 className="footer-newsletter-title">Stay in the Loop</h3>
                <p className="footer-newsletter-description">
                  Subscribe to get updates on upcoming events, exclusive offers,
                  and more.
                </p>
              </div>
              <div className="footer-newsletter-form">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="footer-newsletter-input"
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="footer-newsletter-button"
                >
                  <Send className="footer-newsletter-icon" />
                  <span>Subscribe</span>
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* Main Footer Content */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1,
                  delayChildren: 0.2,
                },
              },
            }}
            className="footer-content"
          >
            {/* Brand Section */}
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.5, ease: "easeOut" },
                },
              }}
              className="footer-brand"
            >
              <div className="footer-brand-header">
                {organization.logo && (
                  <img
                    src={organization.logo}
                    alt={organization.name}
                    className="footer-brand-logo"
                  />
                )}
                <h4 className="footer-brand-name">{organization.name}</h4>
              </div>
              <p className="footer-brand-description">
                Creating unforgettable experiences that bring people together,
                inspire innovation, and foster meaningful connections.
              </p>

              {/* Contact Info */}
              <div className="footer-contact-info">
                {organization.email && (
                  <motion.a
                    href={`mailto:${organization.email}`}
                    whileHover={{ x: 5 }}
                    className="footer-contact-item"
                  >
                    <div className="footer-contact-icon-wrapper">
                      <Mail className="footer-contact-icon" />
                    </div>
                    <span>{organization.email}</span>
                  </motion.a>
                )}
                {organization.phone_number && (
                  <motion.a
                    href={`tel:${organization.phone_number}`}
                    whileHover={{ x: 5 }}
                    className="footer-contact-item"
                  >
                    <div className="footer-contact-icon-wrapper">
                      <Phone className="footer-contact-icon" />
                    </div>
                    <span>{organization.phone_number}</span>
                  </motion.a>
                )}
                {locationString && (
                  <motion.div
                    whileHover={{ x: 5 }}
                    className="footer-contact-item"
                  >
                    <div className="footer-contact-icon-wrapper">
                      <MapPin className="footer-contact-icon" />
                    </div>
                    <span>{locationString}</span>
                  </motion.div>
                )}
              </div>
            </motion.div>

            {/* Links Sections */}
            {[
              {
                title: "Company",
                links: [
                  { label: "About Us", href: "#about" },
                  { label: "Our Team", href: "#team" },
                  { label: "Careers", href: "#careers" },
                  { label: "Press Kit", href: "#press" },
                ],
              },
              {
                title: "Events",
                links: [
                  { label: "Upcoming Events", href: "#upcoming" },
                  { label: "Past Events", href: "#past" },
                  { label: "Virtual Events", href: "#virtual" },
                  { label: "Event Calendar", href: "#calendar" },
                ],
              },
              {
                title: "Resources",
                links: [
                  { label: "Blog", href: "#blog" },
                  { label: "Help Center", href: "#help" },
                  { label: "FAQs", href: "#faq" },
                  { label: "Contact", href: "#contact" },
                ],
              },
              {
                title: "Legal",
                links: [
                  { label: "Privacy Policy", href: "#privacy" },
                  { label: "Terms of Service", href: "#terms" },
                  { label: "Cookie Policy", href: "#cookies" },
                  { label: "Accessibility", href: "#accessibility" },
                ],
              },
            ].map((section, index) => (
              <motion.div
                key={section.title}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.5, ease: "easeOut" },
                  },
                }}
                className="footer-links-section"
              >
                <h4 className="footer-links-title">{section.title}</h4>
                <ul className="footer-links-list">
                  {section.links.map((link) => (
                    <li key={link.label}>
                      <motion.a
                        href={link.href}
                        whileHover={{ x: 5 }}
                        className="footer-link"
                      >
                        <ArrowRight className="footer-link-icon" />
                        <span>{link.label}</span>
                      </motion.a>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>

          {/* Bottom Bar */}
          <div className="footer-bottom">
            {/* Copyright */}
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="footer-copyright"
            >
              <span>
                &copy; {new Date().getFullYear()} {organization.name}. Made with
              </span>
              <Heart className="footer-heart-icon" />
              <span>All rights reserved.</span>
            </motion.p>

            {/* Social Links */}
            {socialLinks.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="footer-social"
              >
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <motion.a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.url}
                      whileHover={{ y: -5, scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className="footer-social-link"
                    >
                      <Icon className="footer-social-icon" />
                    </motion.a>
                  );
                })}
              </motion.div>
            )}
          </div>
        </div>
      </footer>

      {/* Event Details Modal */}
      <AnimatePresence mode="wait">
        {isModalOpen && selectedEvent && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="event-modal-overlay"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, x: "-50%", y: "-50%" }}
              animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
              exit={{ opacity: 0, scale: 0.9, x: "-50%", y: "-50%" }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="event-modal"
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
              }}
            >
              <div className="event-modal-content">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="event-modal-close"
                >
                  <X className="event-modal-close-icon" />
                </button>

                {selectedEvent.image && (
                  <div className="event-modal-image">
                    <img src={selectedEvent.image} alt={selectedEvent.title} />
                  </div>
                )}

                <div className="event-modal-body">
                  <div className="event-modal-header">
                    <h2 className="event-modal-title">{selectedEvent.title}</h2>
                    <div className="event-modal-meta">
                      <span className="event-modal-category">
                        {selectedEvent.category}
                      </span>
                      <span
                        className={`event-modal-pricing ${
                          selectedEvent.isPaid ? "paid" : "free"
                        }`}
                      >
                        {selectedEvent.isPaid ? "Paid Event" : "Free Event"}
                      </span>
                    </div>
                  </div>

                  <div className="event-modal-info">
                    <div className="event-modal-info-item">
                      <Calendar className="event-modal-info-icon" />
                      <div className="event-modal-info-content">
                        <span className="event-modal-info-label">Date</span>
                        <span className="event-modal-info-value">
                          {new Date(selectedEvent.date).toLocaleDateString(
                            "en-US",
                            {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )}
                        </span>
                      </div>
                    </div>

                    <div className="event-modal-info-item">
                      <Clock className="event-modal-info-icon" />
                      <div className="event-modal-info-content">
                        <span className="event-modal-info-label">Time</span>
                        <span className="event-modal-info-value">
                          {selectedEvent.time}
                        </span>
                      </div>
                    </div>

                    <div className="event-modal-info-item">
                      <MapPin className="event-modal-info-icon" />
                      <div className="event-modal-info-content">
                        <span className="event-modal-info-label">Location</span>
                        <span className="event-modal-info-value">
                          {selectedEvent.location}
                        </span>
                      </div>
                    </div>

                    <div className="event-modal-info-item">
                      <Users className="event-modal-info-icon" />
                      <div className="event-modal-info-content">
                        <span className="event-modal-info-label">
                          Attendees
                        </span>
                        <span className="event-modal-info-value">
                          {selectedEvent.attendees}
                          {selectedEvent.maxAttendees
                            ? ` / ${selectedEvent.maxAttendees}`
                            : ""}
                        </span>
                      </div>
                    </div>
                  </div>

                  {selectedEvent.description && (
                    <div className="event-modal-description">
                      <h3 className="event-modal-description-title">
                        About This Event
                      </h3>
                      <p className="event-modal-description-text">
                        {selectedEvent.description}
                      </p>
                    </div>
                  )}

                  <div className="event-modal-actions">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setIsModalOpen(false)}
                      className="event-modal-cancel-btn"
                    >
                      Cancel
                    </motion.button>
                    <motion.a
                      href={`/event/${selectedEvent.id}`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="event-modal-view-btn"
                    >
                      <span>View Full Details</span>
                      <ArrowRight className="event-modal-view-icon" />
                    </motion.a>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

// Event Card Component
interface EventCardProps {
  event: any;
  index: number;
  onViewMore: () => void;
}

const EventCard: React.FC<EventCardProps> = ({ event, index, onViewMore }) => {
  const [isHovered, setIsHovered] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      day: date.getDate(),
      month: date.toLocaleDateString("en-US", { month: "short" }),
      full: date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
    };
  };

  const dateInfo = formatDate(event.date);
  const attendancePercentage = event.maxAttendees
    ? Math.round((event.attendees / event.maxAttendees) * 100)
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{
        duration: 0.6,
        delay: index * 0.1,
        ease: [0.6, 0.05, 0.01, 0.9],
      }}
      whileHover={{ y: -12 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="event-card"
    >
      <div className="event-card-glow" />
      <div className="event-card-inner">
        {/* Event Image with Date Badge */}
        <div className="event-card-image-wrapper">
          <motion.img
            src={event.image || ""}
            alt={event.title}
            className="event-card-image"
            animate={{ scale: isHovered ? 1.08 : 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          />
          <div className="event-card-image-overlay" />

          {/* Date Badge */}
          <motion.div
            className="event-card-date-badge"
            initial={{ scale: 0, rotate: -180 }}
            whileInView={{ scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{
              delay: index * 0.1 + 0.3,
              type: "spring",
              stiffness: 200,
              damping: 15,
            }}
          >
            <div className="event-card-date-month">{dateInfo.month}</div>
            <div className="event-card-date-day">{dateInfo.day}</div>
          </motion.div>
        </div>

        {/* Event Content */}
        <div className="event-card-content">
          <div className="event-card-header">
            <h3 className="event-card-title">{event.title}</h3>
            <p className="event-card-category">{event.category}</p>
          </div>

          <p className="event-card-description">{event.description}</p>

          <div className="event-card-meta">
            <div className="event-card-meta-item">
              <Clock className="event-card-meta-icon" />
              <span>{event.time}</span>
            </div>
            <div className="event-card-meta-item">
              <MapPin className="event-card-meta-icon" />
              <span>{event.location}</span>
            </div>
            <div className="event-card-meta-item">
              <Users className="event-card-meta-icon" />
              <span>
                {event.attendees}
                {event.maxAttendees ? ` / ${event.maxAttendees}` : ""}
              </span>
            </div>
          </div>

          {attendancePercentage !== null && (
            <div className="event-card-progress">
              <div className="event-card-progress-bar">
                <motion.div
                  className="event-card-progress-fill"
                  initial={{ width: 0 }}
                  whileInView={{ width: `${attendancePercentage}%` }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.5, duration: 1 }}
                />
              </div>
              <span className="event-card-progress-text">
                {attendancePercentage}% booked
              </span>
            </div>
          )}

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onViewMore}
            className="event-card-button"
          >
            <span>View Details</span>
            <ArrowRight className="event-card-button-icon" />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};
